import React, { Fragment, memo, useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useSelector } from 'react-redux';
import { Layout, Text, useTheme } from '@ui-kitten/components';
import { UAParser } from 'ua-parser-js';
import lodashIsEmpty from 'lodash/isEmpty';

import Image from '../../../../Components/Image';
import constants from '../../../../Config/constants';
import messages from '../../../../Config/messages';
import { getEnvValue } from '../../../../Helper/env';
import useError from '../../../../Hooks/useError';
import useToast from '../../../../Hooks/useToast';
import {
  productLoadingSelector,
  productSelector,
} from '../../../../RTK/product/selectors';
import { shopSelector } from '../../../../RTK/shop/selectors';
import { isLoggedInSelector } from '../../../../RTK/user/selectors';
import routeList from '../../../../Routes/list';
import ExtraGroup from '../../../../Screens/Product/ExtraGroup';
import ProductLoader from '../../../../Screens/Product/Loader';
import ProductFooter from '../../../../Screens/Product/ProductFooter';
import { RenderItemInfo } from '../../../../Screens/Product/ProductHeading';
import useController from '../../../../Screens/Product/controller';
import ThemeStyle, { globalSpacing } from '../../../../Theme/styles';
import BreakPoints from '../../../../Theme/styles/breakpoints';
import { CartDetails } from '../../../../Types';

import CloseButton from '../../CloseButton';

import ModalPrompt from './ModalPrompt';
import { MODALPROMPT } from './config';
import useModalPrompt from './hooks/useModalPrompt';

interface ItemProps {
  item: CartDetails & {
    allowZero?: boolean;
    fill?: string;
    isAddOn?: boolean;
  };
  route?: any;
  navigation?: any;
}
const Item = () => {
  const RAZZLE_BUILD_MODE = getEnvValue('RAZZLE_BUILD_MODE');
  const store_theme = useTheme();
  const { device } = UAParser();

  const { showModalPrompt, hideModalPrompt } = useModalPrompt();

  const toast = useToast();
  const scrollList = useRef<ScrollView>();
  const shopData = useSelector(shopSelector);
  const isProductLoading = useSelector(productLoadingSelector);
  const productData = useSelector(productSelector);

  const isLogin = useSelector(isLoggedInSelector);
  const [data, setData] = useState<ItemProps>();

  const { item, route, navigation } = data || {
    item: {
      isUpdate: false,
      item_id: '',
      storeImage: '',
      storeName: '',
    },
  };
  const { isUpdate, item_id, storeImage, storeName } = item;
  const availableExtras = productData?.extra_group;
  // memoizing the params is important, or else the product controller will trigger an infinite trigger of the effect since every rerender the object is a new reference so it needs to be memoized
  const memoizedParams = useMemo(
    () => ({
      params: {
        id: item_id,
        is_exclusive: Boolean(productData?.is_exclusive),
        ...item,
        isUpdate,
      },
      scrollHandlers: scrollList,
    }),
    [item_id, isUpdate],
  );
  const { ErrorCartOrCheckoutHandler } = useError(shopData?.id);
  const {
    state,
    getItemTotalPrice,
    addOrRemoveExtras,
    toggleExtrasErrorPrompt,
    getSelectedExtra,
    onAddToCartPressed,
    errorMessage,
    setErrorMessage,
  } = useController(memoizedParams);

  const isMobileView = device.type === 'mobile';

  const _extras = (item, index) => {
    let defaultSelected = state.isUpdate ? getSelectedExtra(item) : null;
    const isRequired = item?.is_required;

    if (lodashIsEmpty(item)) {
      return null;
    }

    return (
      <Fragment key={index}>
        <ExtraGroup
          store_theme={store_theme}
          defaultSelected={defaultSelected}
          group={{ ...item, index }}
          onGroupSelection={addOrRemoveExtras}
          onError={toggleExtrasErrorPrompt}
          onLayout={(event) => {
            const layout = event.nativeEvent.layout;
            state.extrasCoords[index] = layout.y;
          }}
        />
        {errorMessage && isRequired && (
          <View style={styles.errorTags}>
            <Text category="p2" status="danger">
              {errorMessage}
            </Text>
          </View>
        )}
      </Fragment>
    );
  };

  const _onPressClosed = () => {
    setErrorMessage('');
    hideModalPrompt(MODALPROMPT.itemModal);
  };

  const onDone = (data) => {
    if (data.type === 'Success') {
      if (route?.name === routeList.SEARCH) {
        toast.show(messages.ADDED_CART_ITEM, true);
      }
      return hideModalPrompt(MODALPROMPT.itemModal);
    } else {
      return ErrorCartOrCheckoutHandler({
        error: data,
        isAcceptingAdvanceOrder: shopData?.is_accepting_in_advanced_orders,
        store_id: item.store_id,
      });
    }
  };

  const _renderListItem = (
    <Layout level="1">
      {/* storeName AND storeImage is pass on store page item result */}
      {!lodashIsEmpty(storeName) && (
        <View style={styles.storeHeader}>
          <Image
            source={{
              uri: storeImage,
            }}
            style={styles.storeImage}
          />
          <Text
            category="s1"
            numberOfLines={1}
            style={ThemeStyle.spacingRightSmall}
          >
            {storeName}
          </Text>

          <View style={styles.storeCloseContainer}>
            <CloseButton onPress={_onPressClosed} />
          </View>
        </View>
      )}

      <View style={styles.img_container}>
        {
          //@ts-ignore: JS code error
          <Image
            source={{ uri: productData?.image }}
            resizeMode={'cover'}
            style={[styles.img_size, isMobileView && styles.img_border]}
          />
        }
        {lodashIsEmpty(storeName) && (
          <View style={styles.closeButtonContainer}>
            <CloseButton onPress={_onPressClosed} />
          </View>
        )}
      </View>

      <RenderItemInfo
        data={productData}
        stock={state.stocks.remainingStock}
        style={[
          ThemeStyle.pageVerticalSpacingSmall,
          ThemeStyle.pageHorizontalSpacing,
        ]}
      />
    </Layout>
  );

  const _addToCartPress = (isRemove: boolean) => {
    if (!isLogin && RAZZLE_BUILD_MODE !== 'branded' && !item.isAddOn) {
      hideModalPrompt(MODALPROMPT.itemModal);
      showModalPrompt(MODALPROMPT.authentication, {
        isLogin: true,
        navigation: navigation,
      });
      return;
    }
    onAddToCartPressed(scrollList.current, onDone, isRemove);
  };

  return (
    <ModalPrompt
      id={MODALPROMPT.itemModal}
      title=""
      mainContentWrapperStyle={isMobileView && styles.mobileMainWrapper}
      childContentContainerStyle={styles.childContentContainerStyle}
      hasHeader={false}
      hasHeaderLine={false}
      closeOnDocumentClick={false}
      footer={
        <View
          style={{
            flex: 1,
          }}
        >
          <ProductFooter
            allowZero={item.allowZero}
            disabled={shopData?.is_preview || state.disableAddOrUpdateButton}
            fill={item.fill}
            isAddOn={item.isAddOn}
            isAvailable={!state.submitting && productData}
            productData={productData}
            stocks={state.stocks}
            totalPrice={getItemTotalPrice}
            addToCartPress={_addToCartPress}
          />
        </View>
      }
      footerStyle={{ marginBottom: 0 }}
      scrollEnabled={false}
      onBeforeShow={setData}
    >
      <ScrollView
        ref={scrollList}
        showsVerticalScrollIndicator={true}
        dataSet={{ media: ids.scrollWrapper }}
        contentContainerStyle={styles.scrollWrapper}
        scrollEventThrottle={constants.scrollThrottle}
        onContentSizeChange={() => {
          if (lodashIsEmpty(errorMessage)) {
            return scrollList.current?.scrollTo({ y: 0, animated: true });
          }
        }}
      >
        {isProductLoading ? (
          <Fragment>
            <ProductLoader />
            <View style={[styles.storeCloseContainer, { top: globalSpacing }]}>
              <CloseButton onPress={_onPressClosed} />
            </View>
          </Fragment>
        ) : (
          <Fragment>
            {_renderListItem}
            {availableExtras?.map(_extras)}
          </Fragment>
        )}
      </ScrollView>
    </ModalPrompt>
  );
};

const { ids, styles } = StyleSheet.create({
  mobileMainWrapper: {
    width: '100vw',
    height: '100dvh',
    borderRadius: 10,
  },
  scrollWrapper: {
    ...ThemeStyle.pageBottomSpace,
    ...ThemeStyle.pageBackground,
    [`@media ${BreakPoints.xs}`]: {
      width: '100%',
    },
    [`@media ${BreakPoints.sm}`]: {
      width: '100%',
    },
    [`@media ${BreakPoints.md}`]: {
      width: 'auto',
    },
    [`@media ${BreakPoints.lg}`]: {
      width: 'auto',
    },
    [`@media ${BreakPoints.xl}`]: {
      width: 'auto',
    },
    [`@media ${BreakPoints.xxl}`]: {
      width: 'auto',
    },
  },
  childContentContainerStyle: {
    paddingHorizontal: 0,
    flex: 2,
  },
  closeButtonContainer: {
    position: 'absolute',
    alignSelf: 'flex-end',
    ...ThemeStyle.spacingTopSmall,
    ...ThemeStyle.spacingRightSmall,
  },
  errorTags: {
    maxWidth: 550,
    ...ThemeStyle.spacingLeft,
    ...ThemeStyle.spacingRight,
    ...ThemeStyle.pageVerticalSpacingSmall,
  },
  img_container: {
    width: '100%',
    height: 250,
    alignItems: 'center',
  },
  img_size: { width: '100%', height: '100%' },
  img_border: { borderRadius: 10 },
  storeHeader: {
    ...ThemeStyle.flexDirectionRowCenter,
    ...ThemeStyle.pageHorizontalSpacing,
    ...ThemeStyle.spacingTopMedium,
    ...ThemeStyle.spacingBottomMedium,
    columnGap: 16,
  },
  storeImage: {
    width: 45,
    height: 45,
    borderRadius: 45,
  },
  storeCloseContainer: {
    position: 'absolute',
    right: 20,
  },
});

export default memo(Item);
